import type { TextDirection } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import { createSelector } from '@reduxjs/toolkit';

import type { LanguageLocalesEnum } from 'js/src/client';
import { LanguageEnum } from 'js/src/client';
import PlainJsStoreORM from 'js/src/data/plain-js-store-orm';
import { getOriginalTranscriptLanguage } from 'js/src/data/transcript/transcript-selectors';
import { TypeOfTranscript } from 'js/src/data/transcript/transcript-types';
import { getTranslatedLangCodeFromSceneGroupId } from 'js/src/data/translated-editors';
import type {
    ReadonlySupportedLanguage,
    TranslationSelectValue,
} from 'js/src/data/translations/translations-types';
import type { RootState } from 'js/src/libs/RootState';
import { stringifyUnknown } from 'js/src/libs/custom-utils';

export const getVideoTranslationBySceneId = (sceneId: string) => (state: RootState) => {
    return new PlainJsStoreORM(state.translations.translations).getById(sceneId)?.message;
};

export const doesSceneHaveTranslations = (sceneId: string) => (state: RootState) => {
    const text = getVideoTranslationBySceneId(sceneId)(state);
    return !!text;
};

export const hasLoadedSupportedLanguages = (state: RootState) => {
    return state.translations.supported_languages.length > 0;
};

export const getLanguageName = (state: RootState, langCode: string | undefined | null) => {
    if (!langCode) {
        return undefined;
    }
    if (!hasLoadedSupportedLanguages(state)) {
        return undefined;
    }

    const supported_languages = getSupportedLanguages(state);
    const langItem = supported_languages.find((langItem) => {
        return langItem.code === langCode;
    });
    return langItem && langItem.name;
};

export function getSupportedLanguages(state: RootState) {
    return state.translations.supported_languages;
}

export const getSpeechRecognitionLanguagesSelectOptions = createSelector(
    getSupportedLanguages,
    (supportedLanguages) => {
        return supportedLanguages
            .filter((l) => {
                return l.show_as_speech_recognition_language;
            })
            .map((l) => {
                return {
                    label: l.name,
                    value: l.code,
                };
            });
    },
);

export const getLanguageItem = (state: RootState, langCode: string | null) => {
    if (!langCode) {
        return undefined;
    }
    if (!hasLoadedSupportedLanguages(state)) {
        return undefined;
    }
    const supportedLanguages = getSupportedLanguages(state);
    const langItem = supportedLanguages.find((langItem) => langItem.code === langCode);
    return langItem;
};

export const getLanguageReadingSpeed = (state: RootState, langCode: string | null) => {
    const langItem = getLanguageItem(state, langCode);
    return langItem && langItem.reading_speed;
};

export const getLanguageTextDirection = (state: RootState, langCode: string) => {
    const langItem = getLanguageItem(state, langCode);
    if (langItem) {
        return langItem.text_direction as TextDirection;
    }
    return VideoData.LEFT_TO_RIGHT_TEXT_DIRECTION;
};

export const shouldSeparateWordsWithSpaces = (
    state: RootState,
    transcriptLanguage: string,
): boolean => {
    const supportedLanguages = getSupportedLanguages(state);
    const languageInfo = supportedLanguages.find(
        (language) => language.code === transcriptLanguage,
    );
    if (languageInfo) {
        return languageInfo.words_separated_by_spaces;
    } else {
        return true;
    }
};

export const shouldSeparateTranscriptWordsWithSpaces = (
    state: RootState,
    sceneGroupId: string,
    typeOfTranscript: TypeOfTranscript,
) => {
    const langCode =
        typeOfTranscript === TypeOfTranscript.MEDIA_TRANSCRIPT_TAB
            ? getOriginalTranscriptLanguage(state, sceneGroupId)
            : getTranslatedLangCodeFromSceneGroupId(state, sceneGroupId);
    return shouldSeparateWordsWithSpaces(state, langCode || LanguageEnum.XX);
};

export const getTranslationSelectValues = createSelector(
    (state: RootState, onlyIncludeAIVoiceoverSupportedLanguages: boolean) =>
        getSupportedLanguages(state),
    (state: RootState, onlyIncludeAIVoiceoverSupportedLanguages: boolean) =>
        onlyIncludeAIVoiceoverSupportedLanguages,
    (
        supportedLanguages: readonly ReadonlySupportedLanguage[],
        onlyIncludeAIVoiceoverSupportedLanguages,
    ) => {
        const a: TranslationSelectValue[] = [];
        const translationLanguages = supportedLanguages.filter((l) => {
            const supportsTranslation = l.supports_translation;
            const supportsAIVoiceover = l.supports_ai_voiceover;
            if (onlyIncludeAIVoiceoverSupportedLanguages) {
                return supportsTranslation && supportsAIVoiceover;
            }
            return supportsTranslation;
        });
        translationLanguages.forEach((lang) => {
            const locales = lang.translation_locales;
            return locales && locales.length > 0
                ? locales.forEach((locale) =>
                      a.push({
                          label: locale.name,
                          value: locale.code,
                      }),
                  )
                : a.push({
                      label: lang.name,
                      value: lang.code as unknown as LanguageLocalesEnum,
                  });
        });
        return a.sort((a: TranslationSelectValue, b: TranslationSelectValue) => {
            const aLabel = stringifyUnknown(a.label);
            const bLabel = stringifyUnknown(b.label);
            if (aLabel < bLabel) {
                return -1;
            }
            if (aLabel > bLabel) {
                return 1;
            }
            return 0;
        });
    },
);
